import {Inject, Injectable} from '@angular/core';
import {ListAnswerTemplateUseCase} from "../../../domain";
import {BehaviorSubject, combineLatest, map, Subject, throwError} from "rxjs";
import {AnswerTemplateDto} from "../domain/dtos";
import {buildCompanyDisplay, companyFilter, observablesToFilterFormat, textFilter} from "../helpers";
import {LiveChatStateService} from "../../../../../modules/live-chat/services/live-chat-state.service";
import {Company, COMPANY_SERVICE, CompanyService, USER_SERVICE, UserService} from "@livechat/shared";
import {LoggerService} from "../../../../../core/services/logger.service";
import {LogEvent} from "../../../../../core/enums/log-event";
import {livechatTemplatesChosenAdapter} from "../../../../../modules/live-chat/services/logger-adapters";
import {removeColorsFromHTML} from "../../../../../shared/utils/remove-colors-html";

type PersistentFilter = {
  companyId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnswerTemplatesFacade {

  private readonly ANSWER_TEMPLATE_FILTER_KEY = 'answerTemplateFilter'

  private answerTemplatesCache?: Array<AnswerTemplateDto>;

  private _answerTemplates$ = new BehaviorSubject<Array<AnswerTemplateDto>>([]);
  private _onTemplateApplied$ = new Subject<string>();

  private _companyFilter$ = new BehaviorSubject<string | undefined>(undefined);
  private _textFilter$ = new BehaviorSubject<string>('');

  get answerTemplates$() {
    this.loadTemplatesIfNecessary();

    return combineLatest([this._answerTemplates$, this.textFilter$, this.companyFilter$]).pipe(

      // Maps the properties to a friendly format
      map(observablesToFilterFormat),

      // Apply company filter
      map(companyFilter),

      // Apply text filter
      map(textFilter),

      // Builds the companies display
      map(buildCompanyDisplay(this.getCompanies() || [])),

      // Returns only the answer templates list
      map(({ templates }) => {
        return this.answerTemplatesCache ? templates : undefined;
      }),
    );
  }

  get onTemplateApplied$() {
    return this._onTemplateApplied$.asObservable();
  }

  get companyFilter$() {
    return this._companyFilter$.asObservable();
  }

  get companyFilterValue() {
    return this._companyFilter$.getValue();
  }

  get textFilter$() {
    return this._textFilter$.asObservable();
  }

  constructor(
    @Inject(ListAnswerTemplateUseCase) private listAnswerTemplates: ListAnswerTemplateUseCase,
    @Inject(COMPANY_SERVICE) private companyService: CompanyService,
    @Inject(USER_SERVICE) private userService: UserService,
    private liveChatState: LiveChatStateService,
    private logger: LoggerService,
  ) {
    this.loadPersistentFilters();
  }

  public userCanAccessManageScreen() {
    return this.userService.isCurrentUserGlobal() ||
      this.userService.isCurrentUserManager();
  }

  private loadTemplatesIfNecessary() {
    if (!this.answerTemplatesCache) {
      this.refreshList();
    }
  }

  public refreshList() {
    this.listAll().subscribe((templates) => {
      this.answerTemplatesCache = templates;
      this._answerTemplates$.next(this.answerTemplatesCache);
    });
  }

  private listAll() {
    const companiesIds =
      this.userService.isCurrentUserGlobal() ?
        this.userService.getCurrentUser().companiesIds :
        (this.companyService.getCompanies() || []).map(c => c.companyId);

    if (!companiesIds.length) return throwError(() => new Error('No companies found'));

    return this.listAnswerTemplates.execute(companiesIds);
  }

  applyTemplate(template: AnswerTemplateDto) {
    const cleanedHtml = removeColorsFromHTML(template.content);
    this._onTemplateApplied$.next(cleanedHtml);
  }

  getCompanies() {
    return this.companyService.getCompanies();
  }

  getCurrentAttendanceId(): string | undefined {
    return this.liveChatState.currentAttendance?.id;
  }

  getCurrentAttendanceCompany(): Company | undefined {
    const company = this.liveChatState.currentAttendance?.currentCompany;

    if (!company) return;

    return {
      companyId: company.id,
      companyName: company.name
    };
  }

  onOpenAnswerTemplates(companyId?: string) {
    this.filterByCompany(this.hasCompanySavedInFilters() ? companyId : undefined);
  }

  filterByCompany(companyId?: string) {
    this._companyFilter$.next(companyId);
    this.savePersistentFilters(companyId);
  }

  filterByText(query: string) {
    this._textFilter$.next(query);
  }

  private savePersistentFilters(companyId: string | undefined) {
    const userId = this.userService.getCurrentUser().id;
    const filters = JSON.stringify({
      companyId
    });

    localStorage.setItem(`${this.ANSWER_TEMPLATE_FILTER_KEY}.${userId}`, filters);
  }

  private loadPersistentFilters() {
    const filters = this.getFiltersFromLocalStorage();
    if (filters.companyId) {
      const isCompanyAvailable =
        !!((this.getCompanies() || []).find(c => c.companyId === filters.companyId));

      this._companyFilter$.next(isCompanyAvailable ? filters.companyId : undefined);
    }
  }

  private hasCompanySavedInFilters() {
    const filters = this.getFiltersFromLocalStorage();
    return !!filters.companyId;
  }

  private getFiltersFromLocalStorage() {
    const userId = this.userService.getCurrentUser().id;
    return JSON.parse(localStorage.getItem(
      `${this.ANSWER_TEMPLATE_FILTER_KEY}.${userId}`) || '{}'
    ) as PersistentFilter;
  }

}
